import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { surveyLibraryService } from "../services/survey-library.service";

const DEFAULT_FILTERS = {
  search: "",
  type: "all",
  status: "all",
  sort: "recently_updated",
};

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 5,
  total: 0,
  pages: 1,
};

const DEFAULT_STATS = {
  total: 0,
  active: 0,
  submissions: 0,
};

const DEFAULT_SORT = {
  key: "",
  direction: "asc",
};

const buildQueryParams = (filters, pagination, sort) => {
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    sort: filters.sort,
  };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.type !== "all") params.type = filters.type;
  if (filters.status !== "all") params.status = filters.status;
  if (sort?.key) {
    params.sortKey = sort.key;
    params.sortDirection = sort.direction;
  }

  return params;
};

class SurveyLibraryStore {
  surveys = [];
  stats = { ...DEFAULT_STATS };
  filters = { ...DEFAULT_FILTERS };
  pagination = { ...DEFAULT_PAGINATION };
  sort = { ...DEFAULT_SORT };
  loading = false;
  submitting = false;
  initializing = true;
  isModalOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── COMPUTED ─────────────────────────────────────────────────────────
  get canGoPrev() {
    return this.pagination.page > 1;
  }
  get canGoNext() {
    return this.pagination.page < this.pagination.pages;
  }

  get paginationInfo() {
    const { page, limit, total } = this.pagination;
    if (total === 0) return { start: 0, end: 0, total: 0, pages: 1 };
    return {
      start: (page - 1) * limit + 1,
      end: Math.min(page * limit, total),
      total,
      pages: this.pagination.pages,
    };
  }

  // ─── FILTER SETTERS ───────────────────────────────────────────────────
  setSearch = (value) => {
    this.filters.search = value;
    this.pagination.page = 1;
  };
  setType = (value) => {
    this.filters.type = value;
    this.pagination.page = 1;
  };
  setStatus = (value) => {
    this.filters.status = value;
    this.pagination.page = 1;
  };

  // ✅ toolbar dropdown sort (string)
  setDropdownSort = (value) => {
    this.filters.sort = value;
    this.pagination.page = 1;
  };

  // ✅ DataTable column sort ({ key, direction })
  setSort = ({ key, direction }) => {
    this.sort = { key, direction };
    this.pagination.page = 1;
  };

  // ─── PAGINATION ───────────────────────────────────────────────────────
  setPage = (page) => {
    // ✅ was missing
    this.pagination.page = page;
  };

  setRowsPerPage = (limit) => {
    this.pagination.limit = limit;
    this.pagination.page = 1;
  };

  goNextPage = () => {
    if (this.canGoNext) this.pagination.page += 1;
  };
  goPrevPage = () => {
    if (this.canGoPrev) this.pagination.page -= 1;
  };

  // ─── MODAL ────────────────────────────────────────────────────────────
  openCreateModal = () => {
    this.isModalOpen = true;
  };
  closeModal = () => {
    this.isModalOpen = false;
  };

  // ─── INITIALIZE ───────────────────────────────────────────────────────
  initialize = async () => {
    runInAction(() => {
      this.initializing = true;
    });
    await this.fetchSurveys();
    runInAction(() => {
      this.initializing = false;
    });
  };

  // ─── FETCH ────────────────────────────────────────────────────────────
  fetchSurveys = async () => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const params = buildQueryParams(this.filters, this.pagination, this.sort); // ✅ pass sort
      const response = await surveyLibraryService.listSurveys(params);
      const data = response?.data || response;

      runInAction(() => {
        this.surveys = data?.result || [];
        this.pagination = {
          ...this.pagination,
          total: data?.total ?? 0,
          pages: data?.pages ?? 1,
        };
        if (data?.stats) this.stats = data.stats;
      });
    } catch (err) {
      showToast.error(err?.message || t("Unable to load surveys."));
      runInAction(() => {
        this.surveys = [];
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.initializing = false;
      });
    }
  };

  // ─── CREATE ───────────────────────────────────────────────────────────
  createSurvey = async (formData) => {
    runInAction(() => {
      this.submitting = true;
    });
    try {
      const response = await surveyLibraryService.createSurvey(formData);
      const data = response?.data || response;
      const survey = data?.survey || data;
      runInAction(() => {
        this.isModalOpen = false;
      });
      showToast.success(t("Survey created successfully."));
      await this.fetchSurveys();
      return survey;
    } catch (err) {
      showToast.error(err?.message || t("Failed to create survey."));
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };

  // ─── UPDATE ───────────────────────────────────────────────────────────
  updateSurvey = async (id, payload) => {
    runInAction(() => {
      this.submitting = true;
    });
    try {
      const response = await surveyLibraryService.updateSurvey(id, payload);
      const data = response?.data || response;
      const survey = data?.result || data;
      showToast.success(t("Survey updated successfully."));
      return survey;
    } catch (err) {
      showToast.error(err?.message || t("Failed to update survey."));
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };
  // ─── DELETE ───────────────────────────────────────────────────────────
  deleteSurvey = async (id) => {
    try {
      const response = await surveyLibraryService.deleteSurvey(id);
      const deletedSubmissions = response?.deletedSubmissions ?? 0;

      showToast.success(
        deletedSubmissions > 0
          ? t(
              "Survey deleted successfully along with {{count}} submission(s).",
              {
                count: deletedSubmissions,
              },
            )
          : t("Survey deleted successfully."),
      );
      await this.fetchSurveys();
    } catch (err) {
      showToast.error(err?.message || t("Failed to delete survey."));
    }
  };

  // ─── CLOSE ────────────────────────────────────────────────────────────
  closeSurvey = async (id) => {
    try {
      await surveyLibraryService.closeSurvey(id);
      showToast.success(t("Survey closed successfully."));
      await this.fetchSurveys();
    } catch (err) {
      showToast.error(err?.message || t("Failed to close survey."));
    }
  };

  // ─── GET BY ID ────────────────────────────────────────────────────────
  fetchSurveyById = async (id) => {
    try {
      const response = await surveyLibraryService.getSurveyById(id);
      const data = response?.data || response;
      return data?.result || data;
    } catch (err) {
      showToast.error(err?.message || t("Failed to load survey."));
      throw err;
    }
  };
}

export const surveyLibraryStore = new SurveyLibraryStore();
